import { type FormEvent, useState } from "react";
import { formatDate } from "@/lib/date";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { useIntegrations } from "@/hooks/useIntegrations";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { GitBranch, Github, Languages, Loader2, Moon, Plug, Settings, Shield, Sun, Unplug } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import i18n from "@/i18n/config";

interface ChangePasswordResponse {
  message: string;
}

function extractApiErrorMessage(err: ApiError, genericError: string): string {
  try {
    const parsed = JSON.parse(err.body) as { detail?: string };
    if (parsed.detail) return parsed.detail;
  } catch {
    // body is not JSON — fall through to generic message
  }
  return genericError;
}

const GIT_PROVIDERS = [
  {
    id: "github",
    name: "GitHub",
    icon: Github,
    descriptionKey: "settings.git.githubDescription",
  },
  {
    id: "gitlab",
    name: "GitLab",
    icon: GitBranch,
    descriptionKey: "settings.git.gitlabDescription",
  },
] as const;

function GitConnectionsSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { providers, loading, refetch } = useIntegrations();
  const [disconnectTarget, setDisconnectTarget] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  function getMyConnection(providerId: string) {
    return providers.find(
      (p) => p.provider === providerId && p.member_id === user?.id,
    );
  }

  async function handleConnect(providerId: string) {
    try {
      const data = await api<{ url: string }>(
        `/api/v2/git-auth/${providerId}/url?redirect_uri=${encodeURIComponent(window.location.origin + "/auth/callback")}`,
      );
      window.location.href = data.url;
    } catch {
      // redirect failed — user stays on page
    }
  }

  async function handleDisconnect() {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    try {
      await api(`/api/v2/git-auth/${disconnectTarget}/disconnect`, { method: "DELETE" });
      setDisconnectTarget(null);
      refetch();
    } catch {
      // silently fail
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">{t("settings.git.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("settings.git.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">{t("settings.git.title")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("settings.git.description")}
        </p>
      </div>
      {GIT_PROVIDERS.map((provider) => {
        const conn = getMyConnection(provider.id);
        const Icon = provider.icon;
        return (
          <Card key={provider.id}>
            <CardHeader className="flex flex-row items-center gap-4 space-y-0 py-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border bg-muted">
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{provider.name}</span>
                  {conn ? (
                    conn.token_valid === false ? (
                      <Badge variant="destructive">{t("settings.git.tokenExpired")}</Badge>
                    ) : (
                      <Badge variant="default">{t("settings.git.connected")}</Badge>
                    )
                  ) : (
                    <Badge variant="secondary">{t("settings.git.notConnected")}</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {conn
                    ? t("settings.git.since", {
                        username: conn.username,
                        date: formatDate(conn.connected_at),
                      })
                    : t(provider.descriptionKey)}
                </p>
              </div>
              {conn ? (
                <Button variant="outline" size="sm" onClick={() => setDisconnectTarget(provider.id)}>
                  <Unplug className="mr-1 h-4 w-4" />
                  {t("settings.git.disconnect")}
                </Button>
              ) : (
                <Button size="sm" onClick={() => handleConnect(provider.id)}>
                  <Plug className="mr-1 h-4 w-4" />
                  {t("settings.git.connect")}
                </Button>
              )}
            </CardHeader>
          </Card>
        );
      })}
      <Dialog open={!!disconnectTarget} onOpenChange={(open) => !open && setDisconnectTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settings.git.disconnectTitle", { provider: disconnectTarget })}</DialogTitle>
            <DialogDescription>
              {t("settings.git.disconnectDescription", { provider: disconnectTarget })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnectTarget(null)}>{t("settings.git.cancel")}</Button>
            <Button variant="destructive" onClick={handleDisconnect} disabled={disconnecting}>
              {disconnecting ? <><Loader2 className="animate-spin" /> {t("settings.git.disconnecting")}</> : t("settings.git.disconnect")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function SettingsPage() {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError(t("settings.security.mismatch"));
      return;
    }

    setLoading(true);
    try {
      await api<ChangePasswordResponse>("/api/v2/auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      setSuccess(t("settings.security.success"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const genericError = t("settings.security.genericError");
      if (err instanceof ApiError) {
        setError(extractApiErrorMessage(err, genericError));
      } else {
        setError(genericError);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        icon={Settings}
        title={t("settings.header.title")}
        subtitle={t("settings.header.subtitle")}
        tone="zinc"
        secondaryGlow={false}
      />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {theme === "dark" ? (
              <Moon className="h-4 w-4" />
            ) : (
              <Sun className="h-4 w-4" />
            )}
            {t("settings.appearance.title")}
          </CardTitle>
          <CardDescription>{t("settings.appearance.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={toggleTheme} className="gap-2">
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
            {theme === "dark" ? t("settings.appearance.toLight") : t("settings.appearance.toDark")}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="h-4 w-4" />
            {t("settings.language.title")}
          </CardTitle>
          <CardDescription>{t("settings.language.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={i18n.resolvedLanguage}
            onValueChange={(value) => i18n.changeLanguage(value)}
          >
            <SelectTrigger className="w-48" aria-label={t("settings.language.title")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">{t("settings.language.english")}</SelectItem>
              <SelectItem value="es">{t("settings.language.spanish")}</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            {t("settings.security.title")}
          </CardTitle>
          <CardDescription>
            {t("settings.security.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">{t("settings.security.currentPassword")}</Label>
              <Input
                id="currentPassword"
                type="password"
                placeholder="••••••••"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t("settings.security.newPassword")}</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t("settings.security.confirmPassword")}</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            {success && <p className="text-sm text-emerald-600 dark:text-emerald-400">{success}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="animate-spin" />
                  {t("settings.security.saving")}
                </>
              ) : (
                t("settings.security.submit")
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <GitConnectionsSection />
    </div>
  );
}
